import { makeStyles } from '@material-ui/styles'
import { useAppSelector } from 'app/store'
import DocumentMeta from 'core/components/DocumentMeta'
import PresetField from 'core/components/PresetField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import usePluginRouter from 'core/hooks/usePluginRouter'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import GroupMultiSelect from 'k8s/components/common/GroupMultiSelect'
import UserMultiSelect from 'k8s/components/common/UserMultiSelect'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import { propEq } from 'ramda'
import React, { useCallback, useMemo } from 'react'
import useReactRouter from 'use-react-router'
import { emptyObj } from 'utils/fp'
import { listRoleBindings, updateRoleBinding } from './actions'
import { IRbacRoleBindingSelector } from './model'
import { roleBindingsSelector } from './selectors'

const useStyles = makeStyles<Theme>((theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: 16,
  },
}))

const defaultParams = {
  users: [],
  groups: [],
}

export default function UpdateRoleBindingPage() {
  const classes = useStyles({})
  const { match, history } = useReactRouter()
  const { currentPluginId } = usePluginRouter()
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )

  const roleBindingId = match?.params?.id
  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const clusterName = k8sPluginGlobalPerfs?.cluster || ''

  const { loading } = useListAction(listRoleBindings, {
    params: { clusterId },
    requiredParams: ['clusterId'],
  })
  const roleBindings = useAppSelector(roleBindingsSelector)

  const roleBinding = useMemo(
    () => roleBindings.find(propEq('id', roleBindingId)) || (emptyObj as IRbacRoleBindingSelector),
    [roleBindings, roleBindingId],
  )

  const { getParamsUpdater } = useParams(defaultParams)

  const { update, updating, reset, error: errorUpdatingRoleBinding } = useUpdateAction(
    updateRoleBinding,
  )

  const submitForm = useCallback(
    async (params) => {
      const { success } = await update({ ...roleBinding, ...params })
      if (success) handleClose()
    },
    [roleBinding],
  )
  const handleClose = () => {
    reset()
    history.push(
      routeManager.routes.kubernetes.accessControl.roleBindings.path({
        cluster: clusterName,
        plugin: currentPluginId,
      }),
    )
  }

  return (
    <>
      <DocumentMeta
        title="Update Role Binding"
        bodyClasses={['form-view']}
        data-testid="update-role-binding-document-meta"
      />

      <ModalForm
        route={routeManager.routes.kubernetes.accessControl.editRoleBindings}
        title="Edit Role Binding"
        onSubmit={submitForm}
        onClose={handleClose}
        loading={loading}
        submitting={updating}
        error={errorUpdatingRoleBinding}
        loadingMessage={loading ? 'Loading role binding...' : 'Submitting form...'}
        submitTitle="Update Role Binding"
        data-testid="update-role-binding-modal-form"
      >
        <>
          <FormFieldSection
            title="Basic Details"
            step={1}
            data-testid="basic-details-form-field-section"
          >
            <PresetField label="Name" value={roleBinding.name} data-testid="name-preset-field" />
            <PresetField
              label="Namespace"
              value={roleBinding.namespace}
              data-testid="namespace-preset-field"
            />
            {roleBinding.roleRef && (
              <PresetField
                label="Role"
                value={roleBinding.roleRef.name}
                data-testid="role-preset-field"
              />
            )}
          </FormFieldSection>
          {roleBinding.users && (
            <FormFieldSection
              title="Assign Users to this Binding"
              step={2}
              data-testid="assign-users-form-field-section"
            >
              <UserMultiSelect
                id="users"
                tooltip="Select users to assign this role"
                onChange={getParamsUpdater('users')}
                initialValue={roleBinding.users}
                data-testid="users-multi-select"
              />
            </FormFieldSection>
          )}
          {roleBinding.groups && (
            <FormFieldSection
              title="Assign Groups to this Binding"
              step={3}
              data-testid="assign-groups-form-field-section"
            >
              <GroupMultiSelect
                id="groups"
                tooltip="Select groups to assign this role"
                onChange={getParamsUpdater('groups')}
                initialValue={roleBinding.groups}
                data-testid="groups-multi-select"
              />
            </FormFieldSection>
          )}
        </>
      </ModalForm>
    </>
  )
}
